import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, UserCog, X } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import {
  assignManager,
  clearDepartmentManager,
  getManagerCandidates,
  getDepartmentManagers,
} from "@/services/adminService";
import { getErrorMessage } from "@/utils/errorHandler";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Assign one manager to many employees.
 *
 * mode "selected"   — the employees picked on the Employees list (`employeeIds`).
 * mode "department" — everyone in a department, and the manager becomes that
 *                     department's ongoing manager: anyone who joins or moves
 *                     into it later is assigned to them.
 *
 * When some employees already have a different manager, the admin must choose
 * whether to replace them — there is deliberately no default.
 */
export default function AssignManagerDialog({
  open,
  onOpenChange,
  mode = "selected",
  employeeIds = [],
  initialDepartment = "",
  onAssigned,
}) {
  const { user: currentUser } = useAuth();
  const isDepartmentMode = mode === "department";

  const [admins, setAdmins] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [managerId, setManagerId] = useState("");
  const [department, setDepartment] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [choice, setChoice] = useState(""); // "" | "skip" | "replace"
  const [saving, setSaving] = useState(false);
  const [clearingRule, setClearingRule] = useState(false);

  const loadOptions = async () => {
    setLoadingOptions(true);
    try {
      const [adminList, deptList] = await Promise.all([
        getManagerCandidates(),
        isDepartmentMode ? getDepartmentManagers() : Promise.resolve([]),
      ]);
      // Same people the Edit Employee picker offers: anyone with an active
      // login (admin or employee), minus the logged-in admin.
      setAdmins(
        (Array.isArray(adminList) ? adminList : [])
          .filter((a) => {
            const isCurrentUser =
              String(a?._id || "") === String(currentUser?.id || "") ||
              String(a?.email || "").toLowerCase() ===
                String(currentUser?.email || "").toLowerCase();
            return a?.can_manage && !isCurrentUser;
          })
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || "")),
      );
      setDepartments(deptList);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load managers"));
    } finally {
      setLoadingOptions(false);
    }
  };

  // Fresh state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setManagerId("");
    setDepartment(isDepartmentMode ? initialDepartment || "" : "");
    setPreview(null);
    setChoice("");
    loadOptions();
  }, [open]);

  const scopeReady = isDepartmentMode ? Boolean(department) : employeeIds.length > 0;
  const target = isDepartmentMode ? { department } : { employee_ids: employeeIds };

  // Preview (dry run) whenever the manager or scope changes.
  useEffect(() => {
    if (!open || !managerId || !scopeReady) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setChoice("");
    assignManager({ manager_id: managerId, ...target, dry_run: true })
      .then((res) => !cancelled && setPreview(res))
      .catch((error) => {
        if (cancelled) return;
        setPreview(null);
        toast.error(getErrorMessage(error, "Failed to preview the assignment"));
      })
      .finally(() => !cancelled && setPreviewLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, managerId, department, employeeIds.join(",")]);

  const selectedDepartment = departments.find((d) => d.name === department);
  const managerName = admins.find((a) => String(a._id) === managerId)?.name || "";

  const needsChoice = (preview?.with_other_manager || 0) > 0;
  const assignCount = preview
    ? preview.without_manager + (choice === "replace" ? preview.with_other_manager : 0)
    : 0;

  const canSubmit = useMemo(() => {
    if (!preview || previewLoading || saving) return false;
    if (needsChoice && !choice) return false;
    // A department rule is worth setting even when nobody needs assigning now.
    return isDepartmentMode || assignCount > 0;
  }, [preview, previewLoading, saving, needsChoice, choice, isDepartmentMode, assignCount]);

  const handleClearRule = async () => {
    if (!selectedDepartment) return;
    setClearingRule(true);
    try {
      await clearDepartmentManager(selectedDepartment._id);
      toast.success(`${selectedDepartment.name} no longer has a department manager.`);
      setDepartments(await getDepartmentManagers());
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove the department manager"));
    } finally {
      setClearingRule(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await assignManager({
        manager_id: managerId,
        ...target,
        replace_existing: choice === "replace",
      });
      const parts = [`${managerName} assigned to ${plural(res.assigned, "employee")}.`];
      if (res.skipped_existing > 0) {
        parts.push(`${plural(res.skipped_existing, "employee")} kept their manager.`);
      }
      if (res.department_rule_set) {
        parts.push(`${managerName} is now the department manager of ${department}.`);
      }
      toast.success(parts.join(" "));
      onOpenChange(false);
      onAssigned?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to assign manager"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            {isDepartmentMode
              ? "Set department manager"
              : `Assign manager to ${plural(employeeIds.length, "employee")}`}
          </DialogTitle>
          <DialogDescription>
            {isDepartmentMode
              ? "Assigns the manager to everyone in the department, and to anyone who joins or moves into it later."
              : "Assigns the same manager to every selected employee."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {isDepartmentMode && (
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={department} onValueChange={setDepartment} disabled={loadingOptions}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingOptions ? "Loading..." : "Select department"} />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d._id} value={d.name}>
                      {d.name} · {plural(d.employee_count, "employee")}
                      {d.manager ? ` · ${d.manager.name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedDepartment?.manager && (
                <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
                  <span>
                    Current department manager:{" "}
                    <span className="font-semibold">{selectedDepartment.manager.name}</span>
                    {!selectedDepartment.manager.active && " (inactive)"}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-destructive"
                    onClick={handleClearRule}
                    disabled={clearingRule}
                  >
                    {clearingRule ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="mr-1 h-3.5 w-3.5" />
                    )}
                    Remove
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Manager</Label>
            <Select value={managerId} onValueChange={setManagerId} disabled={loadingOptions}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loadingOptions
                      ? "Loading managers..."
                      : admins.length === 0
                        ? "Nobody with an active login yet"
                        : "Select manager"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {admins.map((a) => (
                  <SelectItem key={a._id} value={String(a._id)}>
                    {a.name}
                    {a.is_admin ? " · Admin" : a.designation ? ` · ${a.designation}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {previewLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking employees...
            </div>
          )}

          {preview && !previewLoading && (
            <div className="space-y-3 rounded-lg border p-3 text-sm">
              <ul className="space-y-1">
                <li>
                  <span className="font-semibold">{preview.without_manager}</span> without a manager
                </li>
                <li>
                  <span className="font-semibold">{preview.with_other_manager}</span> already have a
                  different manager
                </li>
                {preview.already_assigned > 0 && (
                  <li>
                    <span className="font-semibold">{preview.already_assigned}</span> already report to{" "}
                    {managerName}
                  </li>
                )}
                {preview.skipped_self > 0 && (
                  <li className="text-muted-foreground">
                    {plural(preview.skipped_self, "employee")} skipped — {managerName} can't be their own
                    manager
                  </li>
                )}
              </ul>

              {needsChoice && (
                <div className="space-y-2 border-t pt-3">
                  <Label className="font-semibold">
                    What about employees who already have a manager?
                  </Label>
                  <RadioGroup value={choice} onValueChange={setChoice} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="skip" id="assign-skip" />
                      <Label htmlFor="assign-skip" className="font-normal">
                        Keep their current manager (assign {preview.without_manager})
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="replace" id="assign-replace" />
                      <Label htmlFor="assign-replace" className="font-normal">
                        Replace with {managerName} (assign{" "}
                        {preview.without_manager + preview.with_other_manager})
                      </Label>
                    </div>
                  </RadioGroup>

                  {choice === "replace" && preview.open_reviews_with_other_manager > 0 && (
                    <p className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      {plural(preview.open_reviews_with_other_manager, "open appraisal/PIP")} will move
                      to {managerName}.
                    </p>
                  )}
                </div>
              )}

              {isDepartmentMode && (
                <p className="border-t pt-3 text-muted-foreground">
                  Going forward, anyone who joins or moves into {department} is assigned to{" "}
                  {managerName}.
                </p>
              )}

              {!isDepartmentMode && preview.without_manager + preview.with_other_manager === 0 && (
                <p className="text-muted-foreground">Nothing to change.</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isDepartmentMode
              ? assignCount > 0
                ? `Set & assign ${assignCount}`
                : "Set department manager"
              : `Assign ${assignCount || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
